import { useEffect, useState } from 'react';

// Event: December 6, 2025 at 9:00 AM GMT+3 (Turkey Time)
const EVENT_DATE = new Date('2025-12-06T09:00:00+03:00');

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

function calculateTimeLeft(): TimeLeft {
  const now = new Date();
  const diff = EVENT_DATE.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

type CountdownTimerProps = {
  compact?: boolean;
};

export function CountdownTimer({ compact = false }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!mounted) return null;

  const isEventStarted = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  if (isEventStarted) {
    return (
      <div className={`font-bold text-brand-600 ${compact ? 'text-sm' : 'text-xl'}`}>
        Etkinlik Başladı!
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-sm font-medium">
        <TimeUnit value={timeLeft.days} label="G" />
        <span className="text-gray-400">:</span>
        <TimeUnit value={timeLeft.hours} label="S" />
        <span className="text-gray-400">:</span>
        <TimeUnit value={timeLeft.minutes} label="D" />
        <span className="text-gray-400">:</span>
        <TimeUnit value={timeLeft.seconds} label="S" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-6 flex-wrap">
      <TimeBlock value={timeLeft.days} label="Gün" />
      <TimeSeparator />
      <TimeBlock value={timeLeft.hours} label="Saat" />
      <TimeSeparator />
      <TimeBlock value={timeLeft.minutes} label="Dk" />
      <TimeSeparator />
      <TimeBlock value={timeLeft.seconds} label="Sn" />
    </div>
  );
}

function TimeBlock({ value, label }: { value: number; label: string }) {
  const digits = String(value).padStart(2, '0').split('');
  
  return (
    <div className="flex flex-col items-center">
      <div className="flex gap-0.5 sm:gap-1">
        {digits.map((digit, idx) => (
          <div
            key={idx}
            className="relative w-8 h-11 sm:w-10 sm:h-14 md:w-14 md:h-20 bg-gradient-to-b from-gray-800 to-gray-900 rounded-md sm:rounded-lg shadow-lg overflow-hidden"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <span 
                key={`${label}-${idx}-${digit}`}
                className="text-xl sm:text-2xl md:text-4xl font-bold text-white tabular-nums animate-flip-in"
              >
                {digit}
              </span>
            </div>
            <div className="absolute inset-x-0 top-1/2 h-px bg-black/20" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
          </div>
        ))}
      </div>
      <span className="mt-1 sm:mt-2 text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );
}

function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-center gap-0.5 bg-gray-100 px-2 py-1 rounded">
      <span 
        key={value}
        className="font-mono font-bold text-gray-800 tabular-nums animate-flip-in"
      >
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

function TimeSeparator() {
  return (
    <div className="flex flex-col gap-1 sm:gap-2">
      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-500 rounded-full animate-pulse" />
      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-brand-500 rounded-full animate-pulse" />
    </div>
  );
}

